<script lang="ts">
	import { createPromiseStore } from "../../../core/utils/createPromiseStore.js";
	import type { ProductInquiryInput } from "../../../core/schema/ProductInquiry.js";
	import sendProductInquiry from "../api/mutations/sendProductInquiry.graphql";
	import { mutation } from "../api/graphql.js";
	import Modal from "./Modal.svelte";
	import Form from "./Form/Form.svelte";
	import ContactFields from "./Form/ContactFields.svelte";
	import FormFooter from "./Form/FormFooter.svelte";
	import Button from "./Button.svelte";
	import arrowRight from "../../../core/assets/icons/arrow-right.svg?raw";
	import translations from "./ProductInquiryModal.translations.json";
	import { getTranslate } from "../utils/getTranslate.js";
	import { getGlobalSettings } from "../getGlobalSettings.js";
	import { getTenant } from "../getTenant.js";
	import { readContactFormData } from "./Form/readContactFormData.js";

	export let show = false;
	export let productId = "";
	export let productName = "";

	const translate = getTranslate(translations);
	const { PUBLIC_GRAPHQL_ENDPOINT } = getGlobalSettings();
	const tenant = getTenant();

	const send = createPromiseStore(async (input: ProductInquiryInput) => {
		await mutation<boolean>(PUBLIC_GRAPHQL_ENDPOINT, sendProductInquiry, { input });
	});

	let submitted = false;

	function reset(): void {
		submitted = false;
	}

	$: if (!show) {
		reset();
	}

	async function submit(event: CustomEvent<SubmitEvent>): Promise<void> {
		const form = event.detail.target as HTMLFormElement;
		const input: ProductInquiryInput = {
			...readContactFormData(new FormData(form), tenant.locale),
			productId,
			productName,
		};
		await send.call(input);
		if ($send.status === "success") {
			form.reset();
			submitted = true;
		}
	}
</script>

<Modal
	bind:show
	class="border-lttertiary !bg-ltwhiteout !w-[50rem] !max-w-[calc(100vw-1rem)] !border !p-0"
	headerClass="p-8 pb-0"
	closeLabel={translate("close")}
	noBackdrop
>
	<h2 slot="title" class="text-ltprimary m-0 font-light uppercase tracking-wider md:text-2xl">
		{translate("title")}
	</h2>

	{#if submitted}
		<div class="flex flex-col items-start gap-4 p-8">
			<p class="text-ltprimary font-bahnschrift text-xl font-medium uppercase tracking-wider">
				{translate("thanks")}
			</p>
			<p class="text-ltsecondary text-base/[1.6] tracking-[0.02em]">
				{translate("willBeInTouch")}
			</p>
			<Button
				icon={arrowRight}
				on:click={() => {
					show = false;
				}}
			>
				{translate("understand")}
			</Button>
		</div>
	{:else}
		<p class="text-ltsecondary px-8 pb-8 text-base/[1.6] tracking-[0.02em]">
			{translate("desc")}
		</p>

		<Form on:submit={submit}>
			<ContactFields nameClass="border-y" textareaClass="h-48" />
			<FormFooter
				loading={$send.status === "loading"}
				error={$send.status === "error"}
				rowFrom="lg"
				class="p-6 md:p-8"
				errorClass="mx-8 mb-8"
				disclaimerClass="mx-8 mb-8"
			/>
		</Form>
	{/if}
</Modal>
